import React from 'react';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { PlusCircleIcon } from '@heroicons/react/24/outline';

import { Checkbox } from '@components/Base/Checkbox';
import { CustomColumnDef } from '@components/Base/Table/types';
import User from '@components/Base/UserDetails';
import { convertToTitleCase } from '@components/ProctorNgSessions/SessionTable/helper';
import { Transactions } from '@containers/transactions/types';
import { classNames } from '@utils/classNames';
import { formatDate } from '@utils/dateFormat';
import gripHorizontalIcon from 'app/images/grip-horizontal.svg';
import './styles.css';

export const createColumns = (): CustomColumnDef<Transactions>[] => [
  {
    id: 'Transaction ID',
    header: () => 'Transaction ID',
    accessorKey: 'id',
    enableSorting: false,
    hidden: false,
    cell: ({ row }) => <div>{row.original.id}</div>,
    size: 200,
    className:
      'text-left text-sm font-medium font-roboto text-gray-900 left-[0px] sticky attendee-column shadow-[inset_-1px_0px_0px_0px] shadow-gray-400/20',
    cellClassName:
      'text-left sticky left-[0px] attendee-column shadow-[inset_-1px_0px_0px_0px] z-[1] shadow-gray-400/20',
  },
  {
    id: 'Drive Enrollment ID',
    header: () => 'Drive Enrollment ID',
    accessorKey: 'drive enrollment id',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Candidate Details',
    header: () => 'Candidate Details',
    accessorKey: 'Candidate Details',
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <User
          data={{
            name: row.original.created_by_details?.email || 'NA',
          }}
          size="sm"
          hideImage={false}
          additionalContent={
            row.original.created_by_details?.created_at
              ? formatDate(new Date(row.original.created_by_details.created_at), 'withTime')
              : ''
          }
        />
      );
    },
    size: 260,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'SKU',
    header: () => 'SKU',
    accessorKey: 'SKU',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 200,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Transaction Action',
    header: () => 'Transaction Action',
    accessorKey: 'Transaction Action',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Transaction Date',
    header: () => 'Transaction Date',
    accessorKey: 'Transaction Date',
    enableSorting: false,
    cell: ({ row }) => (
      <div>{row.original?.created_at ? formatDate(new Date(row.original.created_at), 'withTime') : ''}</div>
    ),
    size: 250,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Status',
    header: () => 'Status',
    accessorKey: 'Status',
    enableSorting: false,
    cell: ({ row }) => <div>{row.original.status}</div>,
    size: 200,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'SKU Value',
    header: () => 'SKU Value',
    accessorKey: 'SKU Value',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Amount Paid',
    header: () => 'Amount Paid',
    accessorKey: 'Amount Paid',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Discount Applied',
    header: () => 'Discount Applied',
    accessorKey: 'Discount Applied',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Voucher Code',
    header: () => 'Voucher Code',
    accessorKey: 'Voucher Code',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Country of Residence',
    header: () => 'Country of Residence',
    accessorKey: 'residence',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 210,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'Invoice ID',
    header: () => 'Invoice ID',
    accessorKey: 'Invoice ID',
    enableSorting: false,
    cell: () => <div>-</div>,
    size: 150,
    className: 'text-left text-sm font-medium font-roboto text-gray-900',
    cellClassName: 'text-left',
    hidden: false,
  },
  {
    id: 'menu',
    enableSorting: false,
    accessorKey: 'menu',
    size: 48,
    className: 'text-left right-0 sticky shadow-[inset_0px_0px_0px_-1px] shadow-gray-400/20',
    cellClassName: 'text-center right-0 sticky z-[1] shadow-[inset_0px_0px_0px_-1px] shadow-gray-400/20',
    header: ({ table }) => {
      return (
        <div>
          <Popover className="flex justify-end items-center pr-3">
            <PopoverButton className={classNames('focus:outline-none')}>
              <PlusCircleIcon
                className={classNames('text-black h-5 w-5 focus:outline-none')}
                aria-hidden="true"
                data-testid="hide-column"
              />
            </PopoverButton>
            <Transition>
              <PopoverPanel>
                <div
                  className="bg-white border-solid border-[1px] popover-width font-normal shadow-sm max-h-[480px] overflow-y-auto rounded-md px-4 absolute right-0 top-12"
                  data-testid="column-model"
                >
                  {table.getAllLeafColumns().map((column) => {
                    if (column.id === 'menu') {
                      return null;
                    }
                    return (
                      <div key={column.id} className="h-[40px] w-full flex items-center">
                        <span className="pr-2 w-6 h-6">
                          <img src={gripHorizontalIcon} alt="edit" className="w-full h-full" />
                        </span>
                        <Checkbox
                          label={
                            typeof column.columnDef.header === 'string'
                              ? column.columnDef.header
                              : convertToTitleCase(column.id)
                          }
                          id={column.id}
                          size="md"
                          checked={column.getIsVisible()}
                          onChange={column.getToggleVisibilityHandler()}
                          disabled={false}
                        />
                      </div>
                    );
                  })}
                </div>
              </PopoverPanel>
            </Transition>
          </Popover>
        </div>
      );
    },
  },
];
